import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { Store } from '@ngxs/store';

import { languages, params } from '@app/configurations';
import { STORAGE_KEYS } from '@app/core/models/storage-keys.models';
import { TranslateService } from '@ngx-translate/core';
import { ApolloRoutes } from '../../app-routing.module';
import { Customer } from '../models/customer.models';
import {
  getCookieByKey,
  IsBilledApp,
  IsPortalApp,
  IsSignupApp,
  SetBrowserAgentClass,
  SetOSClass,
} from '../services/utilities.service';
import { GetAccountCount } from '../store/states/accounts/accounts-state.actions';
import { AccountsState } from '../store/states/accounts/accounts.state';
import { GetCustomerCurrencyRate } from '../store/states/currency/currency-state.actions';
import { GetCustomer } from '../store/states/customer/customer-state.actions';
import { CustomerState } from '../store/states/customer/customer.state';
import { GoToAgencyAdmin } from '../store/states/router-nav/router-nav-state.actions';
import { GetUser, GetUserSettings } from '../store/states/user/user-state.actions';
import { GetOAuthPath } from '../utilities/url.utilities';

export const initializeAppFactory = (store: Store, translate: TranslateService) => () => {
  const signUpSync = localStorage.getItem(STORAGE_KEYS.SIGN_UP_SYNC);

  if (signUpSync) {
    localStorage.removeItem(STORAGE_KEYS.SIGN_UP_SYNC);
    localStorage.removeItem(STORAGE_KEYS.CUSTOMER_ID);
    location.replace(`${location.origin}/signup/syncing`);
    return EMPTY;
  }

  const hasXSRFCookie = getCookieByKey('xsrf');

  SetBrowserAgentClass();
  SetOSClass();

  translate.addLangs(Object.values(languages).map((l) => l.toLowerCase()));
  translate.setDefaultLang(languages.ENGLISH.toLowerCase());

  if (IsBilledApp() || IsPortalApp()) {
    return EMPTY;
  }
  // when in signup and there is no xsrf cookie, it means there was no previous login
  if (IsSignupApp() && !hasXSRFCookie) {
    return EMPTY;
  }

  return store
    .dispatch([new GetUser(), ...(IsSignupApp() ? [] : [new GetUserSettings(), new GetCustomerCurrencyRate()])])
    .pipe(
      switchMap(() => store.dispatch(new GetCustomer())),
      switchMap(() =>
        store
          .selectOnce(CustomerState.customer)
          .pipe(
            switchMap((customer) =>
              customer && !IsSignupApp() ? checkAccountsCustomerAccess(store, customer) : of(void 0),
            ),
          ),
      ),
      catchError(() => of(void 0)),
    );
};

function checkAccountsCustomerAccess(store: Store, customer: Customer): Observable<void> {
  return store.dispatch(new GetAccountCount(customer.customerId)).pipe(
    switchMap(() =>
      combineLatest([store.selectOnce(AccountsState.hasAccounts), store.selectOnce(CustomerState.isAgencyAdmin)]),
    ),
    map(([hasAccounts, isAgencyAdmin]) => {
      if (!hasAccounts && isAgencyAdmin) {
        if (
          ApolloRoutes.some(
            (route) => location.pathname.startsWith(`/${route.path}`) && route.data?.noEmptyAgencyAccess,
          )
        ) {
          store.dispatch(new GoToAgencyAdmin(customer));
        }
      }
    }),
  );
}

export function transformLegacyParamsFactory(): () => Promise<any> {
  return (): Promise<any> =>
    new Promise((resolve) => {
      // for google-mc oauth
      if (location.href.includes('/oauth/google-mc/')) {
        window.history.replaceState(null, null, GetOAuthPath(params.CUSTOMER_ID, 'google-mc'));
      }

      // for amazon-sp oauth
      else if (location.href.includes('/oauth/amazon-sp/')) {
        window.history.replaceState(null, null, GetOAuthPath(params.CUSTOMER_ID, 'amazon-sp'));
      }

      resolve(true);
    });
}
